import { TagDto } from '@maia/tags';
import { undefinedOrTransformToDTOCurried } from '@maia/utils';
import { IInsuranceEntity, IProductEntity } from '../shared';
import { CustomerDto } from './customer.dto';
import { PriceDto, PriceWithProvisionDto } from './common/price.dto';
import { AddressDto } from './common/address.dto';
import { IUpsellproductsEntity } from '../entitites/upsellProducts';
import { AnswerDto, QuestionDto } from './question/question.dto';

export class InsuranceDto {
  readonly guid: string;
  readonly product_guid: string;
  readonly status: ReturnType<typeof TagDto.getInsuranceTag>;
  readonly characteristic: string | undefined;
  readonly reference: string | undefined;
  readonly title: string;
  readonly isMutable: boolean;
  readonly is_cancellable: boolean;
  readonly icon: string;
  readonly price: {
    premiumBeforeDiscount: number;
    discountTotal: number;
    premiumAfterDiscount: number;
    tax: number;
    premiumAfterTax: number;
  };
  readonly effectiveDate: Date;
  readonly premiumDueDate: Date;
  readonly modifiedAt: Date;
  readonly createdAt: Date;
  readonly specs: Array<{ label: string; value: string; icon?: string }>;
  readonly customer: CustomerDto;
  readonly attachments: Array<{
    guid: string;
    filename: string;
    mimetype: string;
  }> | null;
  readonly object: {
    guid: string;
    reference: string;
    title: string;
    specs: Array<{
      label: string;
      value: string;
    }>;
  } | null;
  readonly clauses: Array<{
    name: string;
    content: string;
  }>;
  readonly coverages: Array<{
    guid: string;
    title: string;
    icon: string;
    price: PriceDto;
    specs: Array<{
      label: string;
      value: string;
    }>;
  }>;
  readonly isSpecialLimit: boolean;
  readonly acceptanceRemark: string | null;

  constructor(insurance: IInsuranceEntity) {
    this.guid = insurance.guid;
    this.product_guid = insurance.product_guid;
    this.status = TagDto.getInsuranceTag(insurance.status);
    this.characteristic = insurance.characteristic ?? undefined;
    this.reference = insurance.reference ?? undefined;
    this.isMutable = insurance.is_mutable;
    this.is_cancellable = insurance.is_cancellable;
    this.title = insurance.title;
    this.icon = insurance.icon;
    this.price = {
      premiumBeforeDiscount: insurance.price.premium_before_discount,
      discountTotal: insurance.price.discount_total,
      premiumAfterDiscount: insurance.price.premium_after_discount,
      tax: insurance.price.tax,
      premiumAfterTax: insurance.price.premium_after_tax,
    };
    this.effectiveDate = new Date(insurance.effective_date);
    this.premiumDueDate = new Date(insurance.premium_due_date);
    this.modifiedAt = new Date(insurance.modified_at);
    this.createdAt = new Date(insurance.created_at);
    this.specs =
      insurance.specs?.map((spec) => ({
        label: spec.label,
        value: spec.value,
        icon: spec.icon,
      })) ?? [];
    this.customer = new CustomerDto(insurance.customer);
    this.attachments = insurance.attachments;
    this.object =
      insurance?.object && Object.keys(insurance.object).length > 0
        ? {
            guid: insurance.object.guid,
            reference: insurance.object.reference,
            title: insurance.object.title,
            specs: insurance.object.specs,
          }
        : null;
    this.clauses = insurance.clauses;
    this.coverages = insurance.coverages.map((coverage) => ({
      guid: coverage.guid,
      title: coverage.title,
      icon: coverage.icon,
      price: new PriceDto(coverage.price),
      specs: coverage.specs.map((spec) => ({
        label: spec.label,
        value: spec.value,
      })),
    }));
    this.isSpecialLimit = insurance.special_limit ?? false;
    this.acceptanceRemark = insurance.acceptance_remark ?? null;
  }
}

export const undefinedOrInsurance = undefinedOrTransformToDTOCurried(
  (entity: IInsuranceEntity) => new InsuranceDto(entity),
);

export class ProductsDto {
  readonly guid: string;
  readonly name: string;
  readonly productGuid: string;
  readonly isSelected: boolean;
  readonly isComposition: boolean;
  readonly icon: string;
  readonly object:
    | {
        guid: string;
        reference: string;
        specs: Array<{
          reference: string;
          description: string;
          answer: AnswerDto;
        }>;
      }
    | undefined;
  readonly composition?: string;
  readonly minimumPrice: number;
  readonly totalPrice: number;
  readonly totalTax: number;
  readonly price: PriceWithProvisionDto;
  readonly oldPrice: PriceDto;
  readonly questions: Array<QuestionDto>;
  readonly stakeholders: Array<{
    guid: string;
    role: string;
    connectionToProspect: string;
    contact: {
      firstName: string;
      lastName: string;
      dateOfBirth: Date;
      address: AddressDto;
      firstLetters: string;
    };
  }>;
  readonly addOns: Array<QuestionDto> | undefined;
  readonly specs: Array<{
    type: string;
    value: string;
    description: string;
    children: ProductsDto['specs'];
  }>;
  constructor(entity: IProductEntity) {
    this.guid = entity.guid;
    this.name = entity.name;
    this.productGuid = entity.product_guid;
    this.isSelected = entity.is_selected;
    this.isComposition = entity.is_composition;
    this.icon = entity.icon;
    this.object =
      entity?.object && Object.keys(entity.object).length > 0
        ? {
            guid: entity.object.guid,
            reference: entity.object.reference,
            specs: entity.object.specs.map((spec) => ({
              reference: spec.reference,
              description: spec.description,
              answer: new AnswerDto(spec.answer),
            })),
          }
        : undefined;
    this.composition = entity.composition;
    this.minimumPrice = entity.minimum_price;
    this.totalPrice = entity.total_price;
    this.totalTax = entity.total_tax;
    this.price = new PriceWithProvisionDto(entity.price);
    this.oldPrice = new PriceDto(entity.old_price);
    this.questions = entity.questions.flatMap((question) => {
      // TODO: Should this be commented out? I need the card questions in the quotation detail pages
      //   if (question.position === 'card') return [];
      return new QuestionDto(question);
    });
    this.stakeholders = entity.stakeholders.map((stakeholder) => ({
      guid: stakeholder.guid,
      role: stakeholder.role,
      connectionToProspect: stakeholder.connection_to_prospect,
      contact: {
        firstName: stakeholder.contact.first_name,
        lastName: stakeholder.contact.last_name,
        dateOfBirth: new Date(stakeholder.contact.date_of_birth),
        address: new AddressDto(stakeholder.contact.address),
        firstLetters: stakeholder.contact.first_letters,
      },
    }));
    this.addOns = entity.add_ons?.map((addOn) => new QuestionDto(addOn));
    this.specs = entity.specs.map((spec) => new Spec(spec));
  }
}

class Spec {
  readonly type: string;
  readonly value: string;
  readonly children: Array<Spec>;
  readonly description: string;

  constructor(entity: IProductEntity['specs'][number]) {
    this.type = entity.type;
    this.value = entity.value;
    this.description = entity.description;
    this.children = entity.children.map((child) => new Spec(child));
  }
}

export class UpsellProductsDto {
  guid: string;
  code: string;
  name: string;
  icon: string;
  price: number | undefined;
  objectCode: 'OB' | undefined;
  riskPool?: string;

  constructor(entity: IUpsellproductsEntity) {
    this.guid = entity.guid;
    this.code = entity.code;
    this.name = entity.name;
    this.icon = entity.icon_name;
    this.price = entity.minimum_price ?? undefined;
    this.objectCode = entity.object_code ?? undefined;
    this.riskPool = entity.risk_pool ?? undefined;
  }
}
